/* eslint-disable camelcase */
import Vec2 from 'vec2';

import { ARROW_LENGTH_GRANULARITY_IN_INCHES, CORNER_RADIUS_ANGLE_GRANULARITY_IN_DEGREES } from '@common/constants';
import { LineSegment } from '@common/models';

/**
 * Math related helper functions
 *
 */
const MAX_PRECISION = 20;

/**
 * SimplePoint class should not be used unless absolutely necessary, we're using Vec2 in its place
 */
export class SimplePoint {
    x: number;
    y: number;

    constructor(xLoc: number, yLoc: number) {
        if (xLoc == null) {
            this.x = 0;
            this.y = 0;
        } else {
            this.x = xLoc;
            this.y = yLoc;
        }
    }
}

export class SimpleLine {
    point1: Vec2;
    point2: Vec2;

    constructor(p1: Vec2, p2: Vec2) {
        this.point1 = new Vec2(p1.x, p1.y);
        this.point2 = new Vec2(p2.x, p2.y);
    }
}

export class SimpleSize {
    width: number;
    height: number;

    constructor(myWidth: number, myHeight: number) {
        this.width = myWidth;
        this.height = myHeight;
    }
}

export class SimpleShape {
    line1: SimpleLine;
    line2: SimpleLine;
    line3: SimpleLine;
    line4: SimpleLine;

    constructor(l1: SimpleLine, l2: SimpleLine, l3: SimpleLine, l4: SimpleLine) {
        this.line1 = l1;
        this.line2 = l2;
        this.line3 = l3;
        this.line4 = l4;
    }
}

export class SimpleSegment {
    point: number[] | undefined;
    handleIn: number[] | undefined;
    handleOut: number[] | undefined;

    constructor(point: Vec2, handleIn: Vec2, handleOut: Vec2) {
        if (point) {
            this.point = [point.x, point.y];
        }
        if (handleIn) {
            this.handleIn = [handleIn.x, handleIn.y];
        }
        if (handleOut) {
            this.handleOut = [handleOut.x, handleOut.y];
        }
    }
}

export class BoundingBox {
    left: number;
    top: number;
    right: number;
    bottom: number;

    constructor(left = 0, top = 0, right = 0, bottom = 0) {
        this.left = left;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
    }

    /**
     * get the absolute center point of the box
     */
    getAbsoluteCenter(): Vec2 {
        return new Vec2((this.left + this.right) / 2, (this.bottom + this.top) / 2);
    }

    /**
     * gets the relative mid point of the box from the (left, top) corner
     */
    getMidPoint(): Vec2 {
        return new Vec2(this.getWidth() / 2, this.getHeight() / 2);
    }

    getWidth(): number {
        return Math.abs(this.right - this.left);
    }

    getHeight(): number {
        return Math.abs(this.top - this.bottom);
    }

    asPointArray(): Vec2[] {
        return [
            new Vec2(this.left, this.top),
            new Vec2(this.right, this.top),
            new Vec2(this.right, this.bottom),
            new Vec2(this.left, this.bottom),
        ];
    }
}

export const reverseVector = (vector: Vec2): Vec2 => {
    if (!vector) {
        return new Vec2(0, 0);
    }
    return new Vec2(-vector.x, -vector.y);
};

// Line Segment Formula
export const getLineSegmentLength = (point1: Vec2, point2: Vec2): number => {
    const vector = MathFunctions.getVectorBetweenPoints(point1, point2);
    return Math.sqrt(vector.x * vector.x + vector.y * vector.y);
};

export const getTopLeftVector = (vectors: Array<Vec2>): Vec2 => {
    let topLeftVector = vectors[0];
    // -0.15 is fault tolerance on points coordinates
    const faultTolerance = 0.15;

    /*
 //Need to improve more
 vectors.forEach((v: Vec2) => {
     const diffY = Math.abs(tl.y) - Math.abs(v.y);
     if ((tl.y > 0 && v.y < 0) || (tl.y > v.y && (diffY > faultTolerance || diffY < -faultTolerance))) {
         tl = v;
     } else if (tl.x > 0 && v.x < 0) {
         tl = v;
     } else if (tl.x > v.x && diffY < faultTolerance) {
         const diffX = Math.abs(tl.x) - Math.abs(v.x);
         if (diffX > faultTolerance) {
             tl = v;
         }
     }
 });*/

    vectors.forEach((vector: Vec2) => {
        if (
            vector.y < topLeftVector.y - faultTolerance ||
            (Math.abs(vector.y - topLeftVector.y) < faultTolerance && vector.x < topLeftVector.x)
        ) {
            topLeftVector = vector;
        }
    });

    return topLeftVector;
};

/**
 *
 * @param value value to be rounded
 * @param granularity the granularity to use when rounding. When zero, this function returns {@link value} unaltered
 * @returns the rounded value. When {@link granularity} is 0 returns {@link value}
 */
export const roundToNearest = (value: number, granularity: number): number =>
    granularity === 0 ? value : Math.round(value / granularity) * granularity;

export const roundToArrowLengthGranularity = (length: number): number =>
    roundToNearest(length, ARROW_LENGTH_GRANULARITY_IN_INCHES);

export const roundToCornerRadiusGranularity = (radius: number): number =>
    roundToNearest(radius, CORNER_RADIUS_ANGLE_GRANULARITY_IN_DEGREES);

export const convertFromRadiansToDegree = (angle: number): number => {
    return (angle * 180.0) / Math.PI;
};

/**
 * clamps a value to (min <= value <= max).
 * @param value the input to check
 * @param min the minimum allowed value
 * @param max the maximum allowed value
 */
export const clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(value, max));
};

export class MathFunctions {
    static multiplyVectorByScalar(v: Vec2, s: number): Vec2 {
        return new Vec2(v.x * s, v.y * s);
    }

    static addVectors(v1: Vec2, v2: Vec2): Vec2 {
        return new Vec2(v1.x + v2.x, v1.y + v2.y);
    }

    static subtractVectors(v1: Vec2, v2: Vec2): Vec2 {
        return new Vec2(v1.x - v2.x, v1.y - v2.y);
    }

    static deltaVectorFromTwoPoints(v1: Vec2, v2: Vec2, delta: number): Vec2 {
        const diffVec = MathFunctions.getVectorBetweenPoints(v1, v2);
        const magnitude = MathFunctions.getDistanceBetween(v1, v2);
        return MathFunctions.multiplyVectorByScalar(diffVec, delta / magnitude);
    }

    static roundVector(v: Vec2): Vec2 {
        return new Vec2(Math.round(v.x), Math.round(v.y));
    }

    static roundVectorTo(v: Vec2, digits = 0): Vec2 {
        digits = Math.round(digits);
        if (Math.abs(digits) > MAX_PRECISION) {
            digits = Math.sign(digits) * MAX_PRECISION;
        }
        const scale = 10 ** digits;
        return new Vec2(Math.round(v.x * scale) / scale, Math.round(v.y * scale) / scale);
    }

    static roundTo(n: number, digits = 0): number {
        digits = Math.round(digits);
        if (Math.abs(digits) > MAX_PRECISION) {
            digits = Math.sign(digits) * MAX_PRECISION;
        }
        const scale = 10 ** digits;
        return (n * scale) / scale;
    }

    static getVectorBetweenPoints(p1: Vec2, p2: Vec2): Vec2 {
        return new Vec2(p2.x - p1.x, p2.y - p1.y);
    }

    static getDistanceBetween(p1: Vec2, p2: Vec2): number {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    }

    static getMidpoint(p1: Vec2, p2: Vec2): Vec2 {
        //2d:
        if (!p2) {
            return p1;
        }
        if (!p1) {
            return p2;
        }
        return new Vec2((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
    }

    static isHorizontallyAligned(p1: Vec2, p2: Vec2, round?: boolean): boolean {
        const y1 = p1.y;
        const y2 = p2.y;
        if (round) {
            return Math.round(y1) === Math.round(y2);
        }
        return y1 === y2;
    }

    static isVerticallyAligned(p1: Vec2, p2: Vec2, round?: boolean): boolean {
        const x1 = p1.x;
        const x2 = p2.x;
        if (round) {
            return Math.round(x1) === Math.round(x2);
        }
        return x1 === x2;
    }

    static isHorizontallyOrVerticallyAligned(p1: Vec2, p2: Vec2): boolean {
        return MathFunctions.isHorizontallyAligned(p1, p2) || MathFunctions.isVerticallyAligned(p1, p2);
    }

    static getUnitNormal(p1: Vec2, p2: Vec2): Vec2 {
        if (MathFunctions.isVerticallyAligned(p1, p2)) {
            return new Vec2(MathFunctions.normalizeOneDimensional(p2.y - p1.y), 0);
        }
        if (MathFunctions.isHorizontallyAligned(p1, p2)) {
            return new Vec2(0, MathFunctions.normalizeOneDimensional(p2.x - p1.x));
        }
        const slope = -((p2.x - p1.x) / (p2.y - p1.y));
        return MathFunctions.normalizeTwoDimensionalSlop(slope);
    }

    static normalizeOneDimensional(length: number): number {
        if (length > 0) {
            return 1;
        }
        if (length < 0) {
            return -1;
        }
        return length;
    }

    static normalizeTwoDimensionalSlop(slop: number): Vec2 {
        return MathFunctions.normalizeTwoDimensional(new Vec2(1, slop));
    }

    static normalizeTwoDimensional(vector: Vec2): Vec2 {
        const length = MathFunctions.getVectorLength(vector);
        if (length === 0) {
            return new Vec2(0, 0);
        }
        return new Vec2(vector.x / length, vector.y / length);
    }

    static projectVector(a: Vec2, b: Vec2): Vec2 {
        return new Vec2(a.x * b.x, a.y * b.y).divide(a.lengthSquared());
    }

    static getScalarLengthAlong(deltaVector: Vec2, basisVector: Vec2): number {
        if (MathFunctions.isZeroVector(deltaVector) || MathFunctions.isZeroVector(basisVector)) {
            return 0;
        }
        deltaVector = MathFunctions.projectVector(deltaVector, basisVector);
        const unsignedLength = MathFunctions.getVectorLength(deltaVector);
        if (
            MathFunctions.isOppositeSign(deltaVector.x, basisVector.x) ||
            MathFunctions.isOppositeSign(deltaVector.y, basisVector.y)
        ) {
            return -1 * unsignedLength;
        }
        return unsignedLength;
    }

    static getScalarLengthAlongMatchingLinearVector(deltaVector: Vec2, basisVector: Vec2): number {
        if (MathFunctions.isZeroVector(deltaVector) || MathFunctions.isZeroVector(basisVector)) {
            return 0;
        }
        const unsignedLength = MathFunctions.getVectorLength(deltaVector);
        if (
            MathFunctions.isOppositeSign(deltaVector.x, basisVector.x) ||
            MathFunctions.isOppositeSign(deltaVector.y, basisVector.y)
        ) {
            return -1 * unsignedLength;
        }
        return unsignedLength;
    }

    static getVectorLength(vector: Vec2): number {
        return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
    }

    static isOppositeSign(a: number, b: number): boolean {
        return (a > 0 && b < 0) || (a < 0 && b > 0);
    }

    static pointsAreEqual(p1: Vec2, p2: Vec2): boolean {
        return p1.x === p2.x && p1.y === p2.y;
    }

    static transposePoint(p: Vec2): Vec2 {
        return new Vec2(p.y, p.x);
    }

    static reverseVector(v: Vec2): Vec2 {
        if (!v) {
            return new Vec2(0, 0);
        }
        return new Vec2(-v.x, -v.y);
    }

    static getScalarSlope(p1: Vec2, p2?: Vec2): number {
        if (p2) {
            return MathFunctions.getScalarSlope(MathFunctions.getVectorBetweenPoints(p1, p2));
        }
        if (p1.x === 0) {
            return NaN;
        }
        return p1.y / p1.x;
    }

    static isZeroVector(v: Vec2): boolean {
        return v.x === 0 && v.y === 0;
    }

    static linesIntersect(line1: SimpleLine, line2: SimpleLine): boolean {
        const intPoint = MathFunctions.getInterceptPoint(
            MathFunctions.getMidpoint(line1.point1, line1.point2),
            MathFunctions.getVectorBetweenPoints(line1.point1, line1.point2),
            line2.point2,
            MathFunctions.getVectorBetweenPoints(line2.point1, line2.point2)
        );
        if (intPoint != null && isFinite(intPoint.x) && isFinite(intPoint.y)) {
            return (
                MathFunctions.lineContainsLinearPoint(line1, intPoint) &&
                MathFunctions.lineContainsLinearPoint(line2, intPoint)
            );
        }
        return false;
    }

    static linesShareAnEndPoint(line1: SimpleLine, line2: SimpleLine): boolean {
        return (
            (Math.abs(line1.point1.x - line2.point1.x) < 0.1 && Math.abs(line1.point1.y - line2.point1.y) < 0.1) ||
            (Math.abs(line1.point1.x - line2.point2.x) < 0.1 && Math.abs(line1.point1.y - line2.point2.y) < 0.1) ||
            (Math.abs(line1.point2.x - line2.point1.x) < 0.1 && Math.abs(line1.point2.y - line2.point1.y) < 0.1) ||
            (Math.abs(line1.point2.x - line2.point2.x) < 0.1 && Math.abs(line1.point2.y - line2.point2.y) < 0.1)
        );
    }

    static linesIntersectBeyondEndPoints(line1: SimpleLine, line2: SimpleLine): boolean {
        const midPoint = MathFunctions.getMidpoint(line1.point1, line1.point2);
        const vector1 = MathFunctions.getVectorBetweenPoints(line1.point1, line1.point2);
        const vector2 = MathFunctions.getVectorBetweenPoints(line2.point1, line2.point2);
        const interceptPoint = MathFunctions.getInterceptPoint(midPoint, vector1, line2.point2, vector2);
        if (
            interceptPoint &&
            isFinite(interceptPoint.x) &&
            isFinite(interceptPoint.y) &&
            !MathFunctions.linesShareAnEndPoint(line1, line2)
        ) {
            return (
                MathFunctions.lineContainsLinearPoint(line1, interceptPoint) &&
                MathFunctions.lineContainsLinearPoint(line2, interceptPoint)
            );
        }
        return false;
    }

    static lineContainsPoint(line: SimpleLine, point: Vec2, dontRound: boolean, projectLine: boolean): boolean {
        let slopeVector = this.getVectorBetweenPoints(line.point1, line.point2);
        if (!dontRound) {
            slopeVector = this.roundVector(slopeVector);
        }
        if (slopeVector.x === 0) {
            if (projectLine) {
                const ySorted = [point.y, line.point1.y, line.point2.y].sort((a, b) => {
                    return a - b;
                });
                return point.x === line.point1.x && MathFunctions.numberIsBetween(ySorted[1], ySorted[0], ySorted[2]);
            }
            return point.x === line.point1.x && MathFunctions.numberIsBetween(point.y, line.point1.y, line.point2.y);
        }
        const slope = MathFunctions.getScalarSlope(line.point1, line.point2);
        const yInt = MathFunctions.solveForYIntercept(slope, line.point1);
        let yAtX = slope * point.x + yInt; //y = mx+b
        let pointY = point.y;
        if (!dontRound) {
            pointY = Math.round(pointY);
            yAtX = Math.round(yAtX);
        }

        if (!(pointY === yAtX)) {
            return false;
        }
        if (projectLine) {
            return true;
        }
        return MathFunctions.lineContainsLinearPoint(line, point, dontRound);
    }

    static lineContainsLinearPoint(line: SimpleLine, point: Vec2, dontRound?: boolean): boolean {
        const vectorShift = MathFunctions.reverseVector(line.point1);
        let lineVector = MathFunctions.addVectors(line.point2, vectorShift);
        let newLinearPoint = MathFunctions.addVectors(point, vectorShift);
        if (!dontRound) {
            lineVector = MathFunctions.roundVector(lineVector);
            newLinearPoint = MathFunctions.roundVector(newLinearPoint);
        }
        let lineEndsAt = MathFunctions.getVectorLength(lineVector); //0 to that.
        let pointIsAt = MathFunctions.getScalarLengthAlongMatchingLinearVector(newLinearPoint, lineVector);
        if (!dontRound) {
            lineEndsAt = Math.round(lineEndsAt);
            pointIsAt = Math.round(pointIsAt);
        }
        return MathFunctions.numberIsBetween(pointIsAt, 0, lineEndsAt);
    }

    static numberIsBetween(num: number, a: number, b: number): boolean {
        if (a > b) {
            return MathFunctions.numberIsBetween(num, b, a);
        }
        //a <= b:
        return num >= a && num <= b;
    }

    static intersectionOfSimpleLines(line1: SimpleLine, line2: SimpleLine): boolean | Vec2 {
        return this.intersectionOfLines(
            line1.point1.x,
            line1.point1.y,
            line1.point2.x,
            line1.point2.y,
            line2.point1.x,
            line2.point1.y,
            line2.point2.x,
            line2.point2.y
        );
    }

    static intersectionOfVec2Lines(start1: Vec2, end1: Vec2, start2: Vec2, end2: Vec2): boolean | Vec2 {
        return this.intersectionOfLines(start1.x, start1.y, end1.x, end1.y, start2.x, start2.y, end2.x, end2.y);
    }

    /**
     * Determine the intersection point of two line segments
     * (x1,y1) and (x2,y2) make line1
     * (x3,y3) and (x4,y4) make line2
     * Return FALSE if the lines don't intersect.
     * line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
     */
    static intersectionOfLines(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number
    ): boolean | Vec2 {
        // Check if none of the lines are of length 0
        if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
            return false;
        }

        const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

        // Lines are parallel
        if (denominator === 0) {
            return false;
        }

        const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
        const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return false;
        }

        // Return a object with the x and y coordinates of the intersection
        const x = x1 + ua * (x2 - x1);
        const y = y1 + ua * (y2 - y1);

        return new Vec2(x, y);
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    static getInterceptPoint(
        startingPoint: Vec2,
        slopeVector: Vec2,
        interceptLinePoint: Vec2,
        interceptLineSlopeVector: Vec2,
        dontRoundVectors?: boolean
    ): Vec2 | null {
        //projects intercept line to get an answer.
        if (!dontRoundVectors) {
            return this.getInterceptPoint(
                MathFunctions.roundVector(startingPoint),
                MathFunctions.roundVector(slopeVector.normalize(true)),
                MathFunctions.roundVector(interceptLinePoint),
                MathFunctions.roundVector(interceptLineSlopeVector.normalize(true)),
                true
            );
        }

        // same points
        if (this.pointsAreEqual(startingPoint, interceptLinePoint)) {
            return startingPoint;
        }

        // parallel lines (including both vertical)
        if (
            this.pointsAreEqual(slopeVector, interceptLineSlopeVector) ||
            (slopeVector.x === 0 && interceptLineSlopeVector.x === 0)
        ) {
            return null;
        }

        // zero interceptSlopeVector, if the starting line contains interceptLinePoint, then interceptLinePoint is the intercept
        if (this.pointsAreEqual(new Vec2(0, 0), interceptLineSlopeVector)) {
            if (
                MathFunctions.lineContainsPoint(
                    {
                        point1: startingPoint,
                        point2: MathFunctions.addVectors(startingPoint, slopeVector),
                    },
                    interceptLinePoint,
                    true,
                    true
                )
            ) {
                return interceptLinePoint;
            }
            return null;
        }

        // zero slopeVector, same as above, but return startingPoint
        if (this.pointsAreEqual(new Vec2(0, 0), slopeVector)) {
            if (
                MathFunctions.lineContainsPoint(
                    {
                        point1: interceptLinePoint,
                        point2: MathFunctions.addVectors(interceptLinePoint, interceptLineSlopeVector),
                    },
                    startingPoint,
                    true,
                    true
                )
            ) {
                return startingPoint;
            }
            return null;
        }

        // vertical line, infinite slope,
        if (slopeVector.x === 0) {
            const transposedIntercept = MathFunctions.getInterceptPoint(
                MathFunctions.transposePoint(startingPoint),
                MathFunctions.transposePoint(slopeVector),
                MathFunctions.transposePoint(interceptLinePoint),
                MathFunctions.transposePoint(interceptLineSlopeVector),
                true
            );
            if (transposedIntercept) {
                return MathFunctions.transposePoint(transposedIntercept);
            }
            return null;
        }

        const slope = MathFunctions.getScalarSlope(slopeVector);
        if (interceptLineSlopeVector.x === 0) {
            const yValue = MathFunctions.solveForYIntercept(
                slope,
                new Vec2(startingPoint.x - interceptLinePoint.x, startingPoint.y)
            );
            return new Vec2(interceptLinePoint.x, yValue);
        }
        const interceptLineSlope = MathFunctions.getScalarSlope(interceptLineSlopeVector);
        if (slope === interceptLineSlope) {
            return null;
        }
        const startingPointYIntercept = MathFunctions.solveForYIntercept(slope, startingPoint);
        const interceptLineYIntercept = MathFunctions.solveForYIntercept(interceptLineSlope, interceptLinePoint);

        const xVal = (interceptLineYIntercept - startingPointYIntercept) / (slope - interceptLineSlope);
        const yVal = slope * xVal + startingPointYIntercept;

        return new Vec2(xVal, yVal);
    }

    static solveForYIntercept(slope: number, point: Vec2): number {
        let pointYIntercept: number;
        if (point.x === 0 || slope === 0) {
            pointYIntercept = point.y;
        } else {
            pointYIntercept = point.y - slope * point.x;
        }
        return pointYIntercept;
    }

    static calculateRadiusFromArc(arcWidth: number, arcDepth: number): number {
        const halfLength = arcWidth / 2;
        return (arcDepth * arcDepth + halfLength * halfLength) / (2 * arcDepth);
    }

    static calculateArcDepth(arcWidth: number, radius: number): number {
        const halfLength = arcWidth / 2;
        const depthRadical = Math.sqrt(radius * radius - halfLength * halfLength);
        return radius - depthRadical;
    }

    static calculateArcLength(arcWidth: number, arcDepth: number): number {
        const halfLength = arcWidth / 2;
        const radius = (arcDepth * arcDepth + halfLength * halfLength) / (2 * arcDepth);
        let angleInRadians = 2 * (Math.PI / 2 - Math.acos(halfLength / radius));
        if (arcDepth > arcWidth / 2) {
            angleInRadians = Math.PI * 2 - angleInRadians;
        }
        return angleInRadians * radius;
    }

    static calculateAreaOfTriangle(p1: Vec2, p2: Vec2, p3: Vec2): number {
        const side1 = MathFunctions.getDistanceBetween(p1, p2);
        const side2 = MathFunctions.getDistanceBetween(p2, p3);
        const side3 = MathFunctions.getDistanceBetween(p3, p1);

        const s = (side1 + side2 + side3) / 2;

        return Math.sqrt(s * ((s - side1) * (s - side2) * (s - side3)));
    }

    static getLineLength(line: SimpleLine): number {
        return MathFunctions.getDistanceBetween(line.point1, line.point2);
    }

    static getLineVector(line: SimpleLine): Vec2 {
        return MathFunctions.getVectorBetweenPoints(line.point1, line.point2);
    }

    static getInterceptBetweenSimpleLines(line1: SimpleLine, line2: SimpleLine, dontRound = false): Vec2 | null {
        return MathFunctions.getInterceptPoint(
            line1.point1,
            MathFunctions.getLineVector(line1),
            line2.point1,
            MathFunctions.getLineVector(line2),
            dontRound
        );
    }

    static getLineNormal(line: SimpleLine): Vec2 {
        return MathFunctions.getUnitNormal(line.point1, line.point2);
    }

    static getLineMidpoint(line: SimpleLine): Vec2 {
        return MathFunctions.getMidpoint(line.point1, line.point2);
    }

    static isHorizontalLine(line: SimpleLine, round: boolean): boolean {
        return MathFunctions.isHorizontallyAligned(line.point1, line.point2, round);
    }

    static isVerticalLine(line: SimpleLine, round: boolean): boolean {
        return MathFunctions.isVerticallyAligned(line.point1, line.point2, round);
    }

    static calculateAngleFromLines(lineIn: SimpleLine, lineOut: SimpleLine): number {
        const curve1Point = lineIn.point1;
        const angleVertexPoint = lineIn.point2;
        const curve2Point = lineOut.point2;

        const v1 = MathFunctions.reverseVector(
            MathFunctions.getVectorBetweenPoints(curve1Point, angleVertexPoint)
        ).normalize();
        const v2 = MathFunctions.getVectorBetweenPoints(angleVertexPoint, curve2Point).normalize();
        return v1.angleTo(v2);
    }

    static calculateAngleFromRadiusAndChord(radius: number, chordLength: number): number {
        return 2 * Math.asin(chordLength / (2 * radius));
    }

    static calculateOutVectorFromAngle(vectorIn: Vec2, angle: number, counterClockwise: boolean): Vec2 {
        if (counterClockwise) {
            angle = -1 * angle;
        }
        return vectorIn.rotate(angle, 0, true);
    }

    static convertToRadians(angle: number): number {
        return (angle / 360) * 2 * Math.PI;
    }

    static closestToZero(a: number, b: number): number {
        const absA = Math.abs(a);
        const absB = Math.abs(b);
        if (absB < absA) {
            return b;
        }
        return a;
    }

    static getLineBetween(p1: Vec2, p2: Vec2): SimpleLine {
        return new SimpleLine(p1, p2);
    }

    static isSameSign(n1: number, n2: number): boolean {
        if (n1 > 0 && n2 > 0) {
            return true;
        }
        return !!(n1 < 0 && n2 < 0);
    }

    //Returns the interior angle between 2 vectors in degrees
    // https://onlinemschool.com/math/library/vector/angl/
    static angleBetween(v1: Vec2, v2: Vec2): number {
        const dotProduct = v1.x * v2.x + v1.y * v2.y;
        const cosOfTheta = dotProduct / (v1.length() * v2.length());
        return convertFromRadiansToDegree(Math.acos(cosOfTheta));
    }

    static findBoundingBox(points: Array<Vec2>): BoundingBox {
        const boundingBox = new BoundingBox();
        if (!points || points.length === 0) {
            return boundingBox;
        }
        points.forEach((point, index) => {
            boundingBox.left = index === 0 || point.x < boundingBox.left ? point.x : boundingBox.left;
            boundingBox.top = index === 0 || point.y < boundingBox.top ? point.y : boundingBox.top;
            boundingBox.right = index === 0 || point.x > boundingBox.right ? point.x : boundingBox.right;
            boundingBox.bottom = index === 0 || point.y > boundingBox.bottom ? point.y : boundingBox.bottom;
        });
        return boundingBox;
    }

    /**
     * get an array of lines connecting all the points from a polygon in sequence
     * @param points - the array of points making up the polygon
     * @returns array of SimpleLine segments of points[0]->points[1], points[1]->points[2]...
     */
    static getLinesFromPolygonPoints(points: Vec2[]): SimpleLine[] {
        if (!points || points.length === 0) {
            return [];
        }
        return points.map((value, index, array) => {
            if (index < array.length - 1) {
                return new SimpleLine(value, array[index + 1]);
            } else {
                return new SimpleLine(value, array[0]);
            }
        });
    }

    /**
     * tests if the source polygon (array of Vec2 points defining a polygon) intersects with any shape in the polyArray (an array of arrays of Vec2 points)
     *
     * ignoreIndex lets you specify a single shape in the shapeList that will always return false - this is used when the source polygon is from an existing shape in the shapes list and you don't want to consider it a match on itself
     * polyArray = [ [p1, p2, p3], [p10, p20, p30, p40] ]
     * sourcePoly = [ p100, p101, p102, p103]
     * assuming that the source polygon line (p101, p102) intersects with the second polygon line (p30, p40) in the shapes array...
     * result = [ false, true ]
     * NOTE: if the sourcePoly is fully contained in any of the polyArray polygons, it will return true.
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    static polygonCollisions(sourcePoly: Vec2[], polyArray: Vec2[][], ignoreIndex?: number): boolean[] {
        if (polyArray.length === 0) {
            return [];
        }
        if (sourcePoly && sourcePoly.length === 0) {
            return new Array(polyArray.length).fill(false);
        }
        const sourceLines = this.getLinesFromPolygonPoints(sourcePoly);
        // shapeLines is an array (matching shapes) of lines from each shape
        const shapeLines = polyArray.map((onePoly) => {
            return this.getLinesFromPolygonPoints(onePoly);
        });
        return shapeLines.map((targetLines, index) => {
            if (targetLines.length === 0 || (ignoreIndex && ignoreIndex === index)) {
                return false;
            }
            let intersects = false;
            for (const sourceLine of sourceLines) {
                for (const targetLine of targetLines) {
                    if (MathFunctions.linesIntersect(sourceLine, targetLine)) {
                        intersects = true;
                        break; // if we get a match, we can stop checking this shape
                    }
                }
                if (intersects) {
                    break;
                }
            }
            return intersects;
        });
    }

    /**
     * tests if any of one polygon's sides intersect with any of its other sides
     */
    static polygonCollidesWithItself(sourcePoly: Vec2[]): boolean {
        if (sourcePoly.length === 0) {
            return false;
        }
        const sourceLines = this.getLinesFromPolygonPoints(sourcePoly);

        let intersects = false;
        for (const sourceLine of sourceLines) {
            for (const sourceLineOther of sourceLines) {
                if (MathFunctions.linesIntersectBeyondEndPoints(sourceLine, sourceLineOther)) {
                    intersects = true;
                    break; // if we get a match, we can stop checking this shape
                }
            }
            if (intersects) {
                break;
            }
        }
        return intersects;
    }

    // Finds minumum distance from the testPoly to any of the otherPolys
    static minDistanceToOtherPolys(testPoly: Vec2[], otherPolys: Vec2[][]): number {
        let dist = Infinity;
        otherPolys.forEach((otherPoly) => {
            const testDist = this.polyToPolyDistance(testPoly, otherPoly);
            dist = Math.min(dist, testDist);
        });
        return dist;
    }

    static polyToPolyDistance(poly1: Vec2[], poly2: Vec2[]): number {
        const polyPointsToEdges = (p0: Vec2, i: number, poly: Vec2[]): LineSegment => {
            if (i < poly.length - 1) {
                return { startPoint: p0, endPoint: poly[i + 1] };
            } else {
                return { startPoint: p0, endPoint: poly[0] };
            }
        };

        const p1Edges: LineSegment[] = poly1.map(polyPointsToEdges);
        const p2Edges: LineSegment[] = poly2.map(polyPointsToEdges);

        let dist = Infinity;
        p1Edges.forEach((edge1) => {
            p2Edges.forEach((edge2) => {
                dist = Math.min(this.segmentToSegmentDistance(edge1, edge2), dist);
            });
        });

        return dist;
    }

    // Find the minimum distance between two line segments
    // adapted from https://stackoverflow.com/questions/2824478/shortest-distance-between-two-line-segments
    static segmentToSegmentDistance(l1: LineSegment, l2: LineSegment): number {
        const min = Math.min;
        let distance = this.pointSegmentDistance(l1, l2.startPoint);
        distance = min(this.pointSegmentDistance(l1, l2.endPoint), distance);
        distance = min(this.pointSegmentDistance(l2, l1.startPoint), distance);
        distance = min(this.pointSegmentDistance(l2, l1.endPoint), distance);
        return distance;
    }

    static pointSegmentDistance(l: LineSegment, p: Vec2): number {
        const x1 = l.startPoint.x;
        const y1 = l.startPoint.y;
        const x2 = l.endPoint.x;
        const y2 = l.endPoint.y;

        let dx = x2 - x1;
        let dy = y2 - y1;
        if (dx === 0 && dy === 0) {
            return Math.hypot(p.x - x1, p.y - y1);
        }

        //Calculate the t that minimizes the distance.
        const t = ((p.x - x1) * dx + (p.y - y1) * dy) / (dx * dx + dy * dy);

        //See if this represents one of the segment's
        //end points or a point in the middle.
        if (t < 0) {
            dx = p.x - x1;
            dy = p.y - y1;
        } else if (t > 1) {
            dx = p.x - x2;
            dy = p.y - y2;
        } else {
            const near_x = x1 + t * dx;
            const near_y = y1 + t * dy;
            dx = p.x - near_x;
            dy = p.y - near_y;
        }
        return Math.hypot(dx, dy);
    }
}
